/**
 * THE SHIP-APPROVAL BOARD -- issues a tester has signed off, waiting on the
 * owner. Moved from the /app-issues "Awaiting Approval" tab to System →
 * Controls (2026-09-27): approvals are a control, not a queue filter.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { RubyLoader } from '../../components/RubyLoader';
import { consoleApi, ConsoleApprovalRow, rowKeyOf } from '../../services/consoleApi';
import { CONSOLE_THEME as CT } from '../console/theme';

const fmtAgo = (ts: number): string => {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};


/** Minimum hit area. */
const TAP = 44;

const SkeletonRows: React.FC = () => (
  <div className="relative">
    <div className="space-y-3 animate-pulse">
      {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white border border-stone-200 rounded-2xl" />)}
    </div>
    <RubyLoader variant="overlay" />
  </div>
);

/* --------------------------------------------- board 4: the owner's own pile */

/**
 * THE APPROVAL BOARD (mockup board 4) -- the SAME /app-issues queue with the
 * `awaiting_owner_approval` chip pressed, not a separate page. It lives inside
 * this file for that reason: there is one queue, and this is one of its views.
 *
 * It reads `consoleApi.listAwaitingApproval()` rather than `issuesApi.list`
 * because these rows carry run facts -- tester, checks, cost, money-path flag --
 * that the generic issue row has no column for. The set is the same issues.
 *
 * THE REVIEW LINK IS NEVER DISABLED BY A FAILING TEST. The checks are shown so
 * the owner knows what they are looking at; the decision is the owner's and
 * the pipeline does not get a vote. Do not "improve" this into a gate.
 */
export const ApprovalBoard: React.FC = () => {
  const [rows, setRows] = useState<ConsoleApprovalRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    consoleApi.listAwaitingApproval()
      .then((r) => !cancelled && setRows(r))
      .catch((e: any) => !cancelled && setErr(e?.message || 'Could not load the approval queue'));
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{err}</div>;
  }
  if (!rows) {
    return <SkeletonRows />;
  }
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-stone-200 rounded-xl p-12 text-center">
        <Check size={32} className="mx-auto text-stone-300 mb-3" />
        <div className="text-stone-600">Nothing is waiting on you.</div>
      </div>
    );
  }

  const chip = (tone: 'ok' | 'bad' | 'quiet', text: string) => {
    const s = {
      ok:    { background: '#12261A', border: '1px solid #238636', color: CT.green },
      bad:   { background: '#2D1418', border: `1px solid ${CT.ruby}`, color: CT.rubyLight },
      quiet: { background: '#21262D', border: `1px solid ${CT.border}`, color: CT.dim },
    }[tone];
    return (
      <span style={{ ...s, borderRadius: 4, padding: '3px 9px', fontSize: 11 }}>{text}</span>
    );
  };

  return (
    <div style={{
      background: CT.page, border: `1px solid ${CT.border}`, borderRadius: 12,
      padding: 20, fontFamily: CT.mono, color: CT.text,
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: CT.sans, fontSize: 20, fontWeight: 600 }}>
          Waiting for your approval
        </span>
        <span style={{ fontSize: 12, color: CT.dim }}>
          {rows.length} tested and signed off
        </span>
      </div>

      {rows.map((r) => {
        /*
          EVERY FIELD BELOW IS THE SERVICE'S OWN NAME (`toApprovalQueueRow`).
          `null` from the service means nobody produced it, and each one is
          drawn as its own "not recorded" chip rather than skipped -- a missing
          chip reads as "nothing to report", which is how an issue with three
          failing tests got approved as if no checks had run.
        */
        const key = rowKeyOf(r);
        const failing = typeof r.testsFailed === 'number' && r.testsFailed > 0;
        const testsKnown =
          typeof r.testsFailed === 'number' || typeof r.testsPassed === 'number';
        return (
          <div
            key={key}
            style={{
              background: CT.panel,
              border: `1px solid ${r.touchesMoneyPath === true ? '#5C1A28' : CT.border}`,
              borderRadius: 8, boxSizing: 'border-box', padding: '18px 20px',
              display: 'flex', alignItems: 'flex-start', gap: 18,
            }}
          >
            <div style={{ flexGrow: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: CT.sans, fontSize: 16, fontWeight: 600, color: CT.text }}>
                  {r.title || 'Untitled issue'}
                </span>
                <span style={{ fontSize: 12, color: CT.dim }}>#{r.id}</span>
                {/*
                  The stage is ON THE ROW because the queue merges prod rows in
                  and ids repeat across stages -- two rows reading "#423" with
                  nothing to tell them apart is the defect, not the cure.
                */}
                {r.sourceStage && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                    color: r.sourceStage === 'prod' ? CT.rubyLight : CT.dim,
                    border: `1px solid ${r.sourceStage === 'prod' ? CT.ruby : CT.border}`,
                    borderRadius: 3, padding: '1px 6px',
                  }}>
                    {r.sourceStage.toUpperCase()}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: CT.dim, marginBottom: 11 }}>
                {r.testedBy
                  ? <>tested by <span style={{ color: CT.green }}>{r.testedBy}</span></>
                  : <>tester not recorded</>}
                {typeof r.testedAt === 'number' && <> &middot; {fmtAgo(r.testedAt)}</>}
              </div>
              <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
                {r.touchesMoneyPath === true && chip('bad', '⚠ touches billing')}
                {(r.touchesMoneyPath === null || r.touchesMoneyPath === undefined)
                  && chip('quiet', 'billing impact unknown')}
                {!testsKnown && chip('quiet', 'tests not recorded')}
                {failing && chip('bad', `✗ ${r.testsFailed} tests fail`)}
                {typeof r.testsPassed === 'number' && chip('ok', `✓ ${r.testsPassed} tests pass`)}
                {typeof r.fileCount === 'number'
                  ? chip('quiet', `${r.fileCount} files`)
                  : chip('quiet', 'files not recorded')}
                {typeof r.totalCostUsd === 'number'
                  ? chip('quiet', `$${r.totalCostUsd.toFixed(2)}`)
                  : chip('quiet', 'cost not recorded')}
              </div>
            </div>

            {/*
              Real <Link>, always enabled. A failing test above changes the
              chip, never this.

              THE LINK CARRIES THE KEY, NOT THE ID. `prod:423` and `dev:423`
              are different issues; a link built from `id` alone would open
              whichever of them the review screen read first.
            */}
            <Link
              to={`/app-issues/${encodeURIComponent(key)}/review`}
              style={{
                minHeight: TAP, boxSizing: 'border-box', padding: '11px 20px', borderRadius: 6,
                background: CT.ruby, color: '#ffffff', fontSize: 13, fontWeight: 600,
                display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', textDecoration: 'none',
              }}
            >
              Review
            </Link>
          </div>
        );
      })}
    </div>
  );
};

export default ApprovalBoard;
